import {
  browserServerMessageSchema,
  type BrowserClientMessage,
  type BrowserState,
} from '@hub/contracts'
import { buildWsRoomPath, getWsBaseUrl } from '@/lib/collab/ws-url'

/**
 * Socket client for a thread's BrowserSession (PLAN-083): receives state
 * (who is driving, the Live View URL, a pending hand-off) and sends control
 * messages. The live picture itself is Cloudflare Live View, embedded by
 * the panel from `state.liveView.url`.
 */

export type BrowserConnection = 'connecting' | 'connected' | 'disconnected'

export interface BrowserClientEvents {
  state: (state: BrowserState) => void
  connection: (status: BrowserConnection) => void
  error: (message: string) => void
}

const RECONNECT_BASE_MS = 500
const RECONNECT_MAX_MS = 10_000

export class BrowserClient {
  readonly #url: string
  #ws: WebSocket | null = null
  #closed = false
  #reconnectAttempt = 0
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null
  #listeners: { [K in keyof BrowserClientEvents]: Set<BrowserClientEvents[K]> } = {
    state: new Set(),
    connection: new Set(),
    error: new Set(),
  }

  constructor(projectId: string, threadId: string) {
    this.#url = `${getWsBaseUrl()}/${buildWsRoomPath(projectId, 'browser', threadId)}`
    this.#connect()
  }

  on<K extends keyof BrowserClientEvents>(event: K, fn: BrowserClientEvents[K]): () => void {
    this.#listeners[event].add(fn)
    return () => {
      this.#listeners[event].delete(fn)
    }
  }

  #emit<K extends keyof BrowserClientEvents>(
    event: K,
    ...args: Parameters<BrowserClientEvents[K]>
  ): void {
    for (const fn of this.#listeners[event]) (fn as (...a: unknown[]) => void)(...args)
  }

  #connect(): void {
    if (this.#closed) return
    this.#emit('connection', 'connecting')
    const ws = new WebSocket(this.#url)
    this.#ws = ws
    ws.onopen = () => {
      this.#reconnectAttempt = 0
      this.#emit('connection', 'connected')
    }
    ws.onmessage = (ev) => {
      if (typeof ev.data !== 'string') return
      let msg
      try {
        msg = browserServerMessageSchema.parse(JSON.parse(ev.data))
      } catch {
        return
      }
      if (msg.t === 'state') this.#emit('state', msg.state)
      else this.#emit('error', msg.message)
    }
    ws.onclose = () => {
      if (this.#ws === ws) this.#ws = null
      this.#emit('connection', 'disconnected')
      this.#scheduleReconnect()
    }
    ws.onerror = () => ws.close()
  }

  #scheduleReconnect(): void {
    if (this.#closed || this.#reconnectTimer) return
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** this.#reconnectAttempt++)
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null
      this.#connect()
    }, delay)
  }

  send(msg: BrowserClientMessage): void {
    if (this.#ws?.readyState === WebSocket.OPEN) this.#ws.send(JSON.stringify(msg))
  }

  close(): void {
    this.#closed = true
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer)
    this.#reconnectTimer = null
    this.#ws?.close()
    this.#ws = null
  }
}
